/*
 * Telegram-style animated gradient background renderer
 * Extracted from https://github.com/morethanwords/tweb
 * Matches the implementation in telegram-gradient-pack/index.html
 */

function hexToRgb(hex) {
  const offset = hex[0] === '#' ? 1 : 0
  return {
    r: parseInt(hex.slice(offset, offset + 2), 16),
    g: parseInt(hex.slice(offset + 2, offset + 4), 16),
    b: parseInt(hex.slice(offset + 4, offset + 6), 16)
  }
}

export default class ChatBackgroundGradientRenderer {
  constructor() {
    this._width = 50
    this._height = 50
    this._phase = 0
    this._tail = 0
    this._tails = 90
    this._frames = []
    this._animating = false
    this._raf = null

    this._curve = [
      0, 0.25, 0.50, 0.75, 1, 1.5, 2, 2.5, 3, 3.5, 4, 5, 6, 7, 8, 9, 10, 11, 12,
      13, 14, 15, 16, 17, 18, 18.3, 18.6, 18.9, 19.2, 19.5, 19.8, 20.1, 20.4, 20.7,
      21.0, 21.3, 21.6, 21.9, 22.2, 22.5, 22.8, 23.1, 23.4, 23.7, 24.0, 24.3, 24.6,
      24.9, 25.2, 25.5, 25.8, 26.1, 26.3, 26.4, 26.5, 26.6, 26.7, 26.8, 26.9, 27
    ]

    this._positions = [
      { x: 0.80, y: 0.10 }, { x: 0.60, y: 0.20 },
      { x: 0.35, y: 0.25 }, { x: 0.25, y: 0.60 },
      { x: 0.20, y: 0.90 }, { x: 0.40, y: 0.80 },
      { x: 0.65, y: 0.75 }, { x: 0.75, y: 0.40 }
    ]
    this._phases = this._positions.length

    const diff = this._tails / this._curve[this._curve.length - 1]
    for (let i = 0; i < this._curve.length; i++) {
      this._curve[i] *= diff
    }
    this._incrementalCurve = this._curve.map((v, i, arr) => v - (arr[i - 1] || 0))
  }

  init(canvas) {
    const colors = canvas.getAttribute('data-colors').split(',')
    this._colors = colors.map(c => hexToRgb(c))

    this._hc = document.createElement('canvas')
    this._hc.width = this._width
    this._hc.height = this._height
    this._hctx = this._hc.getContext('2d', { alpha: false })

    this._canvas = canvas
    this._ctx = canvas.getContext('2d', { alpha: false })
    canvas.width = this._width
    canvas.height = this._height

    this._update()
  }

  _getPositions(shift) {
    const pos = this._positions.slice()
    pos.push(...pos.splice(0, shift))
    const result = []
    for (let i = 0; i < pos.length; i += 2) result.push(pos[i])
    return result
  }

  _getNextPositions(phase, curveMax, curve) {
    const pos = this._getPositions(phase)
    if (!curve[0] && curve.length === 1) return [pos]
    const nextPos = this._getPositions(++phase % this._phases)
    const distances = nextPos.map((np, idx) => ({
      x: (np.x - pos[idx].x) / curveMax,
      y: (np.y - pos[idx].y) / curveMax
    }))
    return curve.map(value => distances.map((d, idx) => ({
      x: pos[idx].x + d.x * value,
      y: pos[idx].y + d.y * value
    })))
  }

  _curPosition(phase, tail) {
    return this._getNextPositions(phase, this._tails, [tail])[0]
  }

  _changeTail(diff) {
    this._tail += diff
    while (this._tail >= this._tails) {
      this._tail -= this._tails
      if (++this._phase >= this._phases) this._phase -= this._phases
    }
    while (this._tail < 0) {
      this._tail += this._tails
      if (--this._phase < 0) this._phase += this._phases
    }
  }

  _getGradientImageData(phase, progress) {
    if (phase === undefined) phase = this._phase
    if (progress === undefined) progress = 1 - this._tail / this._tails

    const id = this._hctx.createImageData(this._width, this._height)
    const pixels = id.data
    const colorsLength = this._colors.length

    const positionsForPhase = (ph) => {
      const result = []
      for (let i = 0; i < 4; i++) {
        const p = { ...this._positions[(ph + i * 2) % this._positions.length] }
        p.y = 1.0 - p.y
        result.push(p)
      }
      return result
    }

    const previousPhase = (phase + 1) % this._positions.length
    const previous = positionsForPhase(previousPhase)
    const current = positionsForPhase(phase)

    let offset = 0
    for (let y = 0; y < this._height; y++) {
      const directPixelY = y / this._height
      const centerDistanceY = directPixelY - 0.5
      const centerDistanceY2 = centerDistanceY * centerDistanceY
      for (let x = 0; x < this._width; x++) {
        const directPixelX = x / this._width
        const centerDistanceX = directPixelX - 0.5
        const centerDistance = Math.sqrt(centerDistanceX * centerDistanceX + centerDistanceY2)

        const swirlFactor = 0.35 * centerDistance
        const theta = swirlFactor * swirlFactor * 0.8 * 8.0
        const sinTheta = Math.sin(theta)
        const cosTheta = Math.cos(theta)

        const pixelX = Math.max(0, Math.min(1, 0.5 + centerDistanceX * cosTheta - centerDistanceY * sinTheta))
        const pixelY = Math.max(0, Math.min(1, 0.5 + centerDistanceX * sinTheta + centerDistanceY * cosTheta))

        let distanceSum = 0, r = 0, g = 0, b = 0
        for (let i = 0; i < colorsLength; i++) {
          const colorX = previous[i].x + (current[i].x - previous[i].x) * progress
          const colorY = previous[i].y + (current[i].y - previous[i].y) * progress
          const distX = pixelX - colorX
          const distY = pixelY - colorY
          let distance = Math.max(0, 1.2 - Math.sqrt(distX * distX + distY * distY))
          distance = distance * distance * distance * distance
          distanceSum += distance
          r += distance * this._colors[i].r
          g += distance * this._colors[i].g
          b += distance * this._colors[i].b
        }

        pixels[offset++] = r / distanceSum
        pixels[offset++] = g / distanceSum
        pixels[offset++] = b / distanceSum
        pixels[offset++] = 0xFF
      }
    }
    return id
  }

  _drawImageData(id) {
    this._hctx.putImageData(id, 0, 0)
    this._ctx.drawImage(this._hc, 0, 0, this._width, this._height)
  }

  _drawGradient() {
    this._drawImageData(this._getGradientImageData())
  }

  _update() {
    if (this._colors.length < 2) {
      const c = this._colors[0]
      this._ctx.fillStyle = `rgb(${c.r},${c.g},${c.b})`
      this._ctx.fillRect(0, 0, this._width, this._height)
      return
    }
    this._drawGradient(this._curPosition(this._phase, this._tail))
  }

  // 持续慢速流动动画 (~15fps)
  startAnimation() {
    if (this._colors.length < 2) return
    this._fps = 15
    this._frametime = 1000 / this._fps
    this._lastTs = 0
    const loop = (now) => {
      this._raf = requestAnimationFrame(loop)
      if (!document.hasFocus() || this._animating) return
      if (now - this._lastTs < this._frametime) return
      this._lastTs = now
      this._changeTail(1)
      const pos = this._curPosition(this._phase, this._tail)
      this._drawGradient(pos)
    }
    this._raf = requestAnimationFrame(loop)
  }

  stopAnimation() {
    if (this._raf) {
      cancelAnimationFrame(this._raf)
      this._raf = null
    }
  }

  // 发送消息时触发的平滑加速流动 (easeOutQuad)
  toNextPositionSmooth(duration = 800) {
    if (this._colors.length < 2 || this._animating) return
    this._animating = true

    const totalTails = this._tails
    const startTime = performance.now()
    let lastEasedTail = 0

    const easeOutQuad = (t) => t * (2 - t)

    const animate = (now) => {
      const elapsed = now - startTime
      const rawProgress = Math.min(1, elapsed / duration)
      const easedProgress = easeOutQuad(rawProgress)
      const targetTail = totalTails * easedProgress
      const diff = targetTail - lastEasedTail

      if (diff > 0) {
        lastEasedTail = targetTail
        this._changeTail(diff)
        this._drawGradient()
      }

      if (rawProgress < 1) {
        requestAnimationFrame(animate)
      } else {
        this._animating = false
      }
    }
    requestAnimationFrame(animate)
  }

  // 快速切换到下一阶段（点击/发送消息时触发）
  toNextPosition() {
    if (this._colors.length < 2 || this._animating) return

    const tail = this._tail
    const tails = this._tails
    let nextPhaseOnIdx

    const curve = []
    for (let i = 0; i < this._incrementalCurve.length; i++) {
      const inc = this._incrementalCurve[i]
      let value = (curve[i - 1] !== undefined ? curve[i - 1] : tail) + inc
      if (+value.toFixed(2) > tails && nextPhaseOnIdx === undefined) {
        nextPhaseOnIdx = i
        value %= tails
      }
      curve.push(value)
    }

    const currentPhaseCurve = curve.slice(0, nextPhaseOnIdx)
    const nextPhaseCurve = nextPhaseOnIdx !== undefined ? curve.slice(nextPhaseOnIdx) : []

    this._frames = []
    ;[currentPhaseCurve, nextPhaseCurve].forEach((crv, idx, curves) => {
      const last = crv[crv.length - 1]
      if (last !== undefined && last > tails) crv[crv.length - 1] = +last.toFixed(2)
      this._tail = last || 0
      if (!crv.length) return

      const positions = this._getNextPositions(this._phase, tails, crv)
      if (idx !== curves.length - 1) {
        if (++this._phase >= this._phases) this._phase -= this._phases
      }
      positions.forEach(() => this._frames.push(this._getGradientImageData()))
    })

    this._animating = true
    const animate = () => {
      const frame = this._frames.shift()
      if (frame) {
        this._drawImageData(frame)
        if (this._frames.length) {
          requestAnimationFrame(animate)
        } else {
          this._animating = false
        }
      } else {
        this._animating = false
      }
    }
    requestAnimationFrame(animate)
  }

  cleanup() {
    this.stopAnimation()
    this._animating = false
    this._frames = []
  }
}
